import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { batch, useDispatch, useSelector } from "react-redux";

import {
  BraintreeReduxActions,
  CheckoutAction,
  OrderReduxAction,
  RootState,
} from "../index";
import { checkIfOrderNotFoundError } from "../modules/Order/utils";
import {
  OrderFinalisationStatusType,
  OrderFulfilmentStatusType,
} from "../redux_store/checkout/model";
import { ErrorResponse, HTTPStatusCode } from "../redux_store/error/models";
import { SentryLoggerInstance } from "../sentry";
import { GetOrderResponse } from "../services/api/order/model";
import { timeoutCode } from "../services/httpClient";

const ERROR_RETRY_COUNT = 6;
const ERROR_RETRY_DELAY = 10000;
export const HANDLER_ERROR_ORDER_FAILED_NO_RETRIES =
  "useHandleOrderError: no retries left or order failed, use same order ID to checkout";
export const HANDLER_ERROR_RETRY_SAME_ORDER =
  "useHandleOrderError: got getOrderError while fetching order statuses (Order not placed popup shown):";

interface OrderErrorHandlerProps {
  orderId: string | undefined;
  isProcessing: boolean;
  onHandleFailure: (forceRecreateOrder: boolean) => void;
  onHandleSuccess: (recreateAndSubmitOrder?: boolean) => void;
}

const isOrderDataAvailableAndPossibleToRetryFetching = (
  checkoutReponse: GetOrderResponse,
  retryValue: number
) => {
  return retryValue > 0 && checkoutReponse;
};

export const getStatusCode = (code?: string | number) => {
  if (!code) return 0;
  return typeof code === "string" ? parseInt(code) : code;
};

export const isUnexpectedError = (
  checkoutHasUnexpectedError: boolean,
  error?: ErrorResponse | null | undefined
) => {
  return (
    getStatusCode(error?.statusCode) >= HTTPStatusCode.UNEXPECTEDERROR ||
    checkoutHasUnexpectedError
  );
};

export const isOrderProccessing = (checkoutResponse: GetOrderResponse) => {
  return (
    checkoutResponse?.orderFulfilmentStatus ===
      OrderFulfilmentStatusType.SENT_TO_SITE ||
    checkoutResponse?.orderFulfilmentStatus ===
      OrderFulfilmentStatusType.SENT_TO_KITCHEN ||
    checkoutResponse?.orderFulfilmentStatus ===
      OrderFulfilmentStatusType.RECEIVED_AT_SITE ||
    checkoutResponse?.orderFinalisationStatus ===
      OrderFinalisationStatusType.RECEIVED ||
    checkoutResponse?.orderFinalisationStatus ===
      OrderFinalisationStatusType.COMPLETED
  );
};

export const isOrderStatusFailed = (orderResponse: GetOrderResponse) => {
  return (
    orderResponse?.orderFulfilmentStatus === OrderFulfilmentStatusType.FAILED ||
    orderResponse?.orderFinalisationStatus ===
      OrderFinalisationStatusType.FAILED
  );
};

const isOrderFailedOrNoRetryCountsLeft = (
  orderResponse: GetOrderResponse,
  retriesValue: number
) => {
  return retriesValue === 0 || isOrderStatusFailed(orderResponse);
};

const useHandleOrderError = ({
  orderId,
  isProcessing,
  onHandleFailure,
  onHandleSuccess,
}: OrderErrorHandlerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orderStatus, getOrderError } = useSelector(
    (state: RootState) => state.order
  );
  const retryValue = useRef(ERROR_RETRY_COUNT);
  const isStatusRetriesInProgress = useRef(false);

  const handleError = useCallback(
    (shouldShowErrorPopup: boolean) => {
      dispatch(OrderReduxAction.setOrderLoading(false));
      dispatch(OrderReduxAction.clearOrderResponse());
      //force recreate order without showing popup if order status failed or no retry count left
      onHandleFailure(!shouldShowErrorPopup);
      isStatusRetriesInProgress.current = false;
      if (shouldShowErrorPopup) {
        //Display error message for user so he can try creating order again
        dispatch(
          CheckoutAction.error({
            title: getOrderError?.title ?? t("CheckoutPayment:errorTitle"),
            heading: getOrderError?.heading ?? t("CheckoutPayment:errorTitle"),
            message: getOrderError?.message ?? t("CheckoutPayment:timeoutDesc"),
            statusCode: 0,
          })
        );
      }
    },
    [dispatch, getOrderError, onHandleFailure, t]
  );

  useEffect(() => {
    if (orderId?.length && isProcessing && !isStatusRetriesInProgress.current) {
      // fetch recent order data
      retryValue.current = ERROR_RETRY_COUNT;
      isStatusRetriesInProgress.current = true;

      batch(() => {
        dispatch(CheckoutAction.setHasUnexpectedError(false));
        dispatch(OrderReduxAction.clearOrderError());
        dispatch(OrderReduxAction.clearOrderStatus());
        dispatch(CheckoutAction.resetPayment());
        dispatch(BraintreeReduxActions.setTimeoutError(false));
        dispatch(OrderReduxAction.setOrderLoading(true));
        dispatch(OrderReduxAction.getOrderStatus({ orderId: orderId }));
      });
    }
  }, [dispatch, orderId, isProcessing]);

  const fetchOrderStatusAndUpdateRetryValue = useCallback(() => {
    //fetch order status after delay to get new result from BE
    dispatch(OrderReduxAction.setOrderLoading(true));
    retryValue.current = retryValue.current - 1;
    setTimeout(() => {
      dispatch(OrderReduxAction.getOrderStatus({ orderId: orderId }));
    }, ERROR_RETRY_DELAY);
  }, [dispatch, orderId]);

  useEffect(() => {
    //Check if get order status error is timeout error, retry again without showing any error popup here
    if (
      getOrderError?.statusCode == timeoutCode &&
      isStatusRetriesInProgress.current
    ) {
      //if there no retries left for getting order status, handle success and try resubmit checkout with same payment data
      if (retryValue.current == 0) {
        dispatch(CheckoutAction.setHasUnexpectedError(true));
      } else {
        fetchOrderStatusAndUpdateRetryValue();
      }
    } else if (
      checkIfOrderNotFoundError(getOrderError) &&
      isStatusRetriesInProgress.current
    ) {
      //order not found error, app need to recreate and submit new order with previous payment details
      onHandleSuccess(true);
    } else if (getOrderError && isStatusRetriesInProgress.current) {
      // Only calls handleError when user pressed the `Try Again` when checkout failed
      handleError(true);
      SentryLoggerInstance.sentryCaptureCustomError(
        HANDLER_ERROR_RETRY_SAME_ORDER,
        getOrderError.heading ??
          getOrderError.title + "\n" + getOrderError.message
      );
    }
  }, [
    dispatch,
    fetchOrderStatusAndUpdateRetryValue,
    getOrderError,
    handleError,
    onHandleSuccess,
  ]);

  /**
   * Handles Erros for orders to avoid user to create new order if previous one was accepted
   * Called when order data received onRecreateOrder
   * Checks if order is in progress already and moves user to order status screen by saving success order data
   */
  useEffect(() => {
    //Monitor the status for order and init success payment if order was accepted
    if (isProcessing && isStatusRetriesInProgress.current) {
      if (isOrderProccessing(orderStatus)) {
        isStatusRetriesInProgress.current = false;
        onHandleSuccess();
      } else if (
        isOrderFailedOrNoRetryCountsLeft(orderStatus, retryValue.current)
      ) {
        // if user clicks on pay again, /checkout will be invoked
        // with same order payload
        isStatusRetriesInProgress.current = false;
        onHandleSuccess();
        SentryLoggerInstance.sentryCaptureCustomError(
          HANDLER_ERROR_ORDER_FAILED_NO_RETRIES,
          "ORDER_ID: " +
            orderStatus.orderId +
            `\nRetries left: ${retryValue.current}` +
            `\nisOrderStatusFailed: ${isOrderStatusFailed(orderStatus)}`
        );
      } else if (
        isOrderDataAvailableAndPossibleToRetryFetching(
          orderStatus,
          retryValue.current
        )
      ) {
        fetchOrderStatusAndUpdateRetryValue();
      }
    }
    // TODO: We need to fix this once Order Container is refactored
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, orderStatus]);
};

export default useHandleOrderError;
